import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuthStore } from '@/stores/authStore';
import { useChatStore } from '@/stores/chatStore';
import { useFileStore } from '@/stores/fileStore';
import { api, type TreeNode } from '@/services/api';
import { AlertDialog, Button, ConfirmDialog, RowActionsMenu, TextInputDialog, type RowActionItem } from '@/components/ui';
import { PinToggle } from '@/components/PinToggle';
import { downloadWorkspaceFile } from '@/lib/downloadFile';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  AlertCircle,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  Eye,
  EyeOff,
  File,
  FileCode,
  FileImage,
  FilePlus,
  FileSpreadsheet,
  FileText,
  FileType,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderInput,
  ImagePlus,
  Loader2,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';

interface FileTreeProps {
  className?: string;
  onUploadClick?: (destination?: string, onSuccess?: () => void | Promise<void>) => void;
}

type CreateDialogState = { type: 'file' | 'folder'; parentPath: string } | null;
type RenameDialogState = { node: TreeNode } | null;
type MoveDialogState = { node: TreeNode; destination: string } | null;
type ContextMenuState = { x: number; y: number } | null;

const SYSTEM_ROOT_FOLDERS = new Set(['Dashboards', 'Data', 'Library', 'Reports', 'Scripts']);
const FILE_TREE_CONTEXT_MENU_CLOSE_EVENT = 'telcoos:file-tree-context-menu-close';

function joinPath(parent: string, name: string) {
  return parent ? `${parent}/${name}` : name;
}

function parentPath(path: string) {
  const index = path.lastIndexOf('/');
  return index === -1 ? '' : path.slice(0, index);
}

function fileIcon(node: TreeNode) {
  if (node.type === 'directory') {
    return <Folder className="h-4 w-4 text-primary-600 dark:text-primary-300" />;
  }

  const extension = (node.extension || '').replace(/^\./, '').toLowerCase();
  if (['js', 'jsx', 'ts', 'tsx', 'py', 'rs', 'go', 'java', 'css', 'html', 'json', 'yaml', 'yml', 'sql', 'sh'].includes(extension)) {
    return <FileCode className="h-4 w-4 text-blue-500" />;
  }
  if (['csv', 'tsv', 'xlsx', 'xls', 'parquet'].includes(extension)) {
    return <FileSpreadsheet className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />;
  }
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico', 'bmp'].includes(extension)) {
    return <FileImage className="h-4 w-4 text-fuchsia-500" />;
  }
  if (extension === 'pdf') {
    return <FileType className="h-4 w-4 text-red-500" />;
  }
  if (['md', 'markdown', 'txt', 'log'].includes(extension)) {
    return <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400" />;
  }
  return <File className="h-4 w-4 text-surface-400" />;
}

function isPersonalInstructions(node: TreeNode) {
  return node.path === 'CLAUDE.md' && node.type === 'file';
}

function displayName(node: TreeNode) {
  return isPersonalInstructions(node) ? 'Personal instructions' : node.name;
}

function filterSystemNodes(nodes: TreeNode[], showHidden: boolean): TreeNode[] {
  return nodes
    .filter((node) => {
      const isRoot = !node.path.includes('/');
      if (!showHidden && node.name.startsWith('.')) return false;
      if (!showHidden && isRoot && node.type === 'directory' && SYSTEM_ROOT_FOLDERS.has(node.name)) return false;
      return true;
    })
    .map((node) => ({
      ...node,
      children: node.children ? filterSystemNodes(node.children, showHidden) : node.children,
    }));
}

function fuzzyMatch(query: string, target: string) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const t = target.toLowerCase();
  if (t.includes(q)) return true;

  let offset = 0;
  for (const char of q) {
    offset = t.indexOf(char, offset);
    if (offset === -1) return false;
    offset += 1;
  }
  return true;
}

function searchTree(nodes: TreeNode[], query: string): TreeNode[] {
  if (!query.trim()) return nodes;

  const matches: TreeNode[] = [];
  for (const node of nodes) {
    const childMatches = node.children ? searchTree(node.children, query) : [];
    if (fuzzyMatch(query, `${node.name} ${node.path}`) || childMatches.length > 0) {
      matches.push({
        ...node,
        children: childMatches.length > 0 ? childMatches : node.children,
      });
    }
  }
  return matches;
}

function visiblePaths(nodes: TreeNode[], expandedDirs: Set<string>, forceExpanded: boolean): string[] {
  const paths: string[] = [];
  for (const node of nodes) {
    paths.push(node.path);
    if (node.type === 'directory' && (forceExpanded || expandedDirs.has(node.path)) && node.children) {
      paths.push(...visiblePaths(node.children, expandedDirs, forceExpanded));
    }
  }
  return paths;
}

function selectedUploadDestination(selectedPaths: Set<string>, tree: TreeNode[]) {
  const selectedList = Array.from(selectedPaths);
  const selected = selectedList[selectedList.length - 1];
  if (!selected) return '';

  const find = (nodes: TreeNode[]): TreeNode | null => {
    for (const node of nodes) {
      if (node.path === selected) return node;
      const child = node.children ? find(node.children) : null;
      if (child) return child;
    }
    return null;
  };

  const node = find(tree);
  if (!node) return '';
  return node.type === 'directory' ? node.path : parentPath(node.path);
}

function statusText(node: TreeNode) {
  if (node.type === 'directory') return 'Folder';
  const extension = node.extension || 'file';
  return `${extension} - ${node.size.toLocaleString()} bytes`;
}

function isImageNode(node: TreeNode) {
  const extension = (node.extension || '').replace(/^\./, '').toLowerCase();
  return node.type === 'file' && ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(extension);
}

async function copyTextToClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    // Fall back for browser contexts where Clipboard API exists but is denied.
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);
  const copied = document.execCommand('copy');
  textarea.remove();

  if (!copied) {
    throw new Error('Clipboard copy failed');
  }
}

function FileTreeContextMenu({
  state,
  items,
  onClose,
}: {
  state: ContextMenuState;
  items: RowActionItem[];
  onClose: () => void;
}) {
  useEffect(() => {
    if (!state) return;

    const closeOnOutside = () => onClose();
    const closeOnGlobalContextMenu = () => onClose();
    const closeOnEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEsc);
    window.addEventListener(FILE_TREE_CONTEXT_MENU_CLOSE_EVENT, closeOnGlobalContextMenu);
    window.addEventListener('scroll', closeOnOutside, true);
    window.addEventListener('resize', closeOnOutside);

    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEsc);
      window.removeEventListener(FILE_TREE_CONTEXT_MENU_CLOSE_EVENT, closeOnGlobalContextMenu);
      window.removeEventListener('scroll', closeOnOutside, true);
      window.removeEventListener('resize', closeOnOutside);
    };
  }, [onClose, state]);

  if (!state) return null;

  const menuWidth = 200;
  const menuHeight = items.length * 34 + 8;
  const left = Math.min(Math.max(8, state.x), window.innerWidth - menuWidth - 8);
  const top = Math.max(8, Math.min(state.y, window.innerHeight - menuHeight - 8));

  return createPortal(
    <div
      role="menu"
      className="fixed z-50 rounded-md border border-surface-200 bg-white py-1 shadow-lg dark:border-surface-700 dark:bg-surface-900"
      style={{ left, top, width: menuWidth }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {items.map((item, index) => {
        const Icon = item.icon;
        return (
          <button
            key={index}
            role="menuitem"
            type="button"
            disabled={item.disabled}
            onClick={(event) => {
              event.stopPropagation();
              onClose();
              item.onClick();
            }}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
              item.danger
                ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                : 'text-surface-700 hover:bg-surface-100 dark:text-surface-200 dark:hover:bg-surface-800',
              item.disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {Icon && <Icon className="h-4 w-4 shrink-0" />}
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>,
    document.body,
  );
}

interface TreeNodeRowProps {
  node: TreeNode;
  depth: number;
  allVisiblePaths: string[];
  forceExpanded: boolean;
  onCreate: (state: CreateDialogState) => void;
  onRename: (state: RenameDialogState) => void;
  onMove: (state: MoveDialogState) => void;
  onDelete: (node: TreeNode) => void;
  onOperationError: (message: string) => void;
}

function TreeNodeRow({
  node,
  depth,
  allVisiblePaths,
  forceExpanded,
  onCreate,
  onRename,
  onMove,
  onDelete,
  onOperationError,
}: TreeNodeRowProps) {
  const {
    expandedDirs,
    selectedPaths,
    toggleDir,
    selectPath,
    togglePathSelection,
    selectRange,
    openFile,
  } = useFileStore();
  const activeSessionId = useChatStore((state) => state.activeSessionId);
  const setPendingComposerInsert = useChatStore((state) => state.setPendingComposerInsert);
  const setPendingComposerImages = useChatStore((state) => state.setPendingComposerImages);

  const isDirectory = node.type === 'directory';
  const isExpanded = forceExpanded || expandedDirs.has(node.path);
  const isSelected = selectedPaths.has(node.path);
  const hasChildren = isDirectory && Boolean(node.children?.length);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null);

  const handleOpen = useCallback(async () => {
    if (isDirectory) {
      selectPath(node.path);
      toggleDir(node.path);
      return;
    }

    try {
      await openFile(node.path);
    } catch (err) {
      onOperationError(err instanceof Error ? err.message : 'Failed to open file');
    }
  }, [isDirectory, node.path, onOperationError, openFile, selectPath, toggleDir]);

  const handleClick = useCallback((event: React.MouseEvent) => {
    if (event.shiftKey) {
      event.preventDefault();
      selectRange(node.path, allVisiblePaths);
      return;
    }
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      togglePathSelection(node.path);
      return;
    }
    void handleOpen();
  }, [allVisiblePaths, handleOpen, node.path, selectRange, togglePathSelection]);

  const attachImageToChat = useCallback(() => {
    if (!activeSessionId) return;
    setPendingComposerImages([node.path]);
    toast.success('Attached image to chat composer');
  }, [activeSessionId, node.path, setPendingComposerImages]);

  const insertPathReference = useCallback(() => {
    setPendingComposerInsert(`\`${node.path}\``);
    toast.success('Inserted file path into chat composer');
  }, [node.path, setPendingComposerInsert]);

  const downloadFile = useCallback(() => {
    void downloadWorkspaceFile(node.path, node.name).catch((error) => {
      console.error('File download failed:', error);
      toast.error('Failed to download file');
    });
  }, [node.name, node.path]);

  const copyPath = useCallback(() => {
    void copyTextToClipboard(node.path)
      .then(() => toast.success('Copied workspace path'))
      .catch((error) => {
        console.error('Copy path failed:', error);
        toast.error('Failed to copy path');
      });
  }, [node.path]);

  const actionItems = useMemo<RowActionItem[]>(() => [
    ...(isDirectory ? [
      { label: 'New file', icon: FilePlus, onClick: () => onCreate({ type: 'file', parentPath: node.path }) },
      { label: 'New folder', icon: FolderPlus, onClick: () => onCreate({ type: 'folder', parentPath: node.path }) },
      { label: 'Copy path', icon: Copy, onClick: copyPath },
    ] : [
      ...(isImageNode(node) ? [
        {
          label: 'Attach image to chat',
          icon: ImagePlus,
          disabled: !activeSessionId,
          onClick: attachImageToChat,
        },
      ] : []),
      {
        label: 'Copy path',
        icon: Copy,
        onClick: copyPath,
      },
      {
        label: 'Insert path reference',
        icon: FileText,
        disabled: !activeSessionId,
        onClick: insertPathReference,
      },
      {
        label: 'Download',
        icon: Download,
        onClick: downloadFile,
      },
    ]),
    { label: 'Rename', icon: Pencil, onClick: () => onRename({ node }) },
    { label: 'Move', icon: FolderInput, onClick: () => onMove({ node, destination: parentPath(node.path) }) },
    { label: 'Delete', icon: Trash2, danger: true, onClick: () => onDelete(node) },
  ], [
    activeSessionId,
    attachImageToChat,
    copyPath,
    downloadFile,
    insertPathReference,
    isDirectory,
    node,
    onCreate,
    onDelete,
    onMove,
    onRename,
  ]);

  return (
    <>
      <div
        role="treeitem"
        aria-expanded={isDirectory ? isExpanded : undefined}
        aria-selected={isSelected}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          window.dispatchEvent(new Event(FILE_TREE_CONTEXT_MENU_CLOSE_EVENT));
          selectPath(node.path);
          setContextMenu({ x: event.clientX, y: event.clientY });
        }}
        className={cn(
          'group flex min-w-0 items-center gap-1 px-2 py-1 text-sm',
          isSelected
            ? 'bg-primary-50 text-primary-800 dark:bg-primary-950/40 dark:text-primary-200'
            : 'text-surface-700 hover:bg-surface-100 dark:text-surface-300 dark:hover:bg-surface-800'
        )}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
      >
        <button
          type="button"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-surface-200 dark:hover:bg-surface-700"
          onClick={(event) => {
            event.stopPropagation();
            if (isDirectory) {
              selectPath(node.path);
              toggleDir(node.path);
            }
          }}
          tabIndex={isDirectory ? 0 : -1}
          aria-label={isExpanded ? `Collapse ${node.name}` : `Expand ${node.name}`}
        >
          {isDirectory ? (
            isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <span className="h-3.5 w-3.5" />
          )}
        </button>

        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 rounded px-1 py-1 text-left"
          onClick={handleClick}
          onDoubleClick={() => void handleOpen()}
          title={node.path}
        >
          {isDirectory && isExpanded ? <FolderOpen className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-300" /> : fileIcon(node)}
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{displayName(node)}</span>
            {isPersonalInstructions(node) && (
              <span className="block truncate text-xs text-surface-500 dark:text-surface-400">
                User-editable Claude preferences
              </span>
            )}
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          {!isDirectory && <PinToggle kind="file" entityId={node.path} />}
          <RowActionsMenu
            label={`Actions for ${node.name}`}
            items={actionItems}
          />
        </div>
      </div>

      <FileTreeContextMenu
        state={contextMenu}
        items={actionItems}
        onClose={() => setContextMenu(null)}
      />

      {hasChildren && isExpanded && (
        <div role="group">
          {node.children?.map((child) => (
            <TreeNodeRow
              key={child.path}
              node={child}
              depth={depth + 1}
              allVisiblePaths={allVisiblePaths}
              forceExpanded={forceExpanded}
              onCreate={onCreate}
              onRename={onRename}
              onMove={onMove}
              onDelete={onDelete}
              onOperationError={onOperationError}
            />
          ))}
        </div>
      )}
    </>
  );
}

export function FileTree({ className, onUploadClick }: FileTreeProps) {
  const user = useAuthStore((state) => state.user);
  const isAdmin = Boolean(user?.isAdmin);
  const {
    tree,
    treeLoading,
    treeError,
    expandedDirs,
    selectedPaths,
    showHiddenFiles,
    loadTree,
    refreshTree,
    toggleHiddenFiles,
    clearError,
    createDirectory,
    createFile,
  } = useFileStore();

  const [query, setQuery] = useState('');
  const [createDialog, setCreateDialog] = useState<CreateDialogState>(null);
  const [renameDialog, setRenameDialog] = useState<RenameDialogState>(null);
  const [moveDialog, setMoveDialog] = useState<MoveDialogState>(null);
  const [deleteTarget, setDeleteTarget] = useState<TreeNode | null>(null);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);

  useEffect(() => {
    void loadTree('', 20);
  }, [loadTree]);

  const showSystem = isAdmin && showHiddenFiles;
  const filteredTree = useMemo(() => {
    return searchTree(filterSystemNodes(tree, showSystem), query);
  }, [query, showSystem, tree]);
  const allVisiblePaths = useMemo(() => {
    return visiblePaths(filteredTree, expandedDirs, Boolean(query.trim()));
  }, [expandedDirs, filteredTree, query]);
  const uploadDestination = useMemo(() => {
    return selectedUploadDestination(selectedPaths, filteredTree);
  }, [filteredTree, selectedPaths]);

  const refreshWorkspace = useCallback(async () => {
    await Promise.allSettled([
      refreshTree(),
      api.workspace.assets.reindex(),
    ]);
  }, [refreshTree]);

  const handleRetry = useCallback(() => {
    clearError();
    void refreshTree();
  }, [clearError, refreshTree]);

  const handleCreate = useCallback(async (name: string) => {
    if (!createDialog) return;

    const path = joinPath(createDialog.parentPath, createDialog.type === 'file' && !name.includes('.') ? `${name}.md` : name);
    setIsMutating(true);
    try {
      const success = createDialog.type === 'folder'
        ? await createDirectory(path)
        : await createFile(path, createDialog.type === 'file' ? `# ${name.replace(/\.md$/i, '')}\n` : '');
      if (success) {
        setCreateDialog(null);
        await refreshWorkspace();
      }
    } catch (err) {
      setOperationError(err instanceof Error ? err.message : 'Failed to create item');
    } finally {
      setIsMutating(false);
    }
  }, [createDialog, createDirectory, createFile, refreshWorkspace]);

  const handleRename = useCallback(async (name: string) => {
    if (!renameDialog) return;
    if (name === renameDialog.node.name) {
      setRenameDialog(null);
      return;
    }

    setIsMutating(true);
    try {
      await api.files.move(renameDialog.node.path, joinPath(parentPath(renameDialog.node.path), name));
      setRenameDialog(null);
      await refreshWorkspace();
    } catch (err) {
      setOperationError(err instanceof Error ? err.message : 'Failed to rename item');
    } finally {
      setIsMutating(false);
    }
  }, [refreshWorkspace, renameDialog]);

  const handleMove = useCallback(async () => {
    if (!moveDialog) return;

    const destination = joinPath(moveDialog.destination.trim(), moveDialog.node.name);
    if (destination === moveDialog.node.path) {
      setMoveDialog(null);
      return;
    }

    setIsMutating(true);
    try {
      await api.files.move(moveDialog.node.path, destination);
      setMoveDialog(null);
      await refreshWorkspace();
    } catch (err) {
      setOperationError(err instanceof Error ? err.message : 'Failed to move item');
    } finally {
      setIsMutating(false);
    }
  }, [moveDialog, refreshWorkspace]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;

    setIsMutating(true);
    try {
      await api.files.delete(deleteTarget.path, { recursive: deleteTarget.type === 'directory' });
      setDeleteTarget(null);
      await refreshWorkspace();
    } catch (err) {
      setOperationError(err instanceof Error ? err.message : 'Failed to delete item');
    } finally {
      setIsMutating(false);
    }
  }, [deleteTarget, refreshWorkspace]);

  const createTitle = createDialog?.type === 'folder' ? 'New Folder' : 'New Markdown File';
  const createLabel = createDialog?.type === 'folder' ? 'Folder name' : 'File name';
  const createPlaceholder = createDialog?.type === 'folder' ? 'Folder name' : 'notes.md';

  return (
    <div className={cn('flex h-full min-h-0 flex-col bg-white dark:bg-surface-900', className)}>
      <div className="flex items-center justify-between border-b border-surface-200 px-3 py-2 dark:border-surface-800">
        <div className="flex min-w-0 items-center gap-2">
          <FolderOpen className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-300" />
          <span className="truncate text-xs font-semibold uppercase text-surface-500 dark:text-surface-400">Files</span>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => void toggleHiddenFiles()}
              title={showSystem ? 'Hide system folders and dot-files' : 'Show system folders and dot-files'}
            >
              {showSystem ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCreateDialog({ type: 'file', parentPath: uploadDestination })} title="New file">
            <FilePlus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCreateDialog({ type: 'folder', parentPath: uploadDestination })} title="New folder">
            <FolderPlus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => void refreshWorkspace()} disabled={treeLoading} title="Refresh">
            <RefreshCw className={cn('h-4 w-4', treeLoading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      <div className="border-b border-surface-200 p-2 dark:border-surface-800">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="w-full rounded-md border border-surface-200 bg-surface-50 py-1.5 pl-8 pr-8 text-sm text-surface-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 dark:border-surface-700 dark:bg-surface-950 dark:text-surface-100"
            placeholder="Search files..."
            type="text"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-surface-400 hover:text-surface-700 dark:hover:text-surface-200"
              title="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {onUploadClick && (
        <div className="border-b border-surface-200 p-2 dark:border-surface-800">
          <Button variant="outline" size="sm" className="w-full" onClick={() => onUploadClick(uploadDestination, refreshWorkspace)}>
            <Upload className="h-4 w-4" />
            <span className="ml-2 truncate">Upload</span>
          </Button>
        </div>
      )}

      {treeError && tree.length > 0 && (
        <div className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{treeError}</span>
          <button type="button" className="font-medium hover:underline" onClick={handleRetry}>
            Retry
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto py-1" role="tree" aria-label="Workspace file tree">
        {treeLoading && tree.length === 0 ? (
          <div className="flex items-center gap-2 p-4 text-sm text-surface-500 dark:text-surface-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading files...
          </div>
        ) : treeError && tree.length === 0 ? (
          <div className="p-4 text-sm text-red-600 dark:text-red-400">
            <div className="mb-3 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {treeError}
            </div>
            <Button variant="outline" size="sm" onClick={handleRetry}>
              <RefreshCw className="h-4 w-4" />
              <span className="ml-2">Retry</span>
            </Button>
          </div>
        ) : filteredTree.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 py-8 text-center text-sm text-surface-500 dark:text-surface-400">
            {query ? <Search className="mb-3 h-8 w-8 text-surface-300 dark:text-surface-600" /> : <Folder className="mb-3 h-8 w-8 text-surface-300 dark:text-surface-600" />}
            <p>{query ? `No matches for "${query}"` : 'No files found'}</p>
          </div>
        ) : (
          <>
            {filteredTree.map((node) => (
              <TreeNodeRow
                key={node.path}
                node={node}
                depth={0}
                allVisiblePaths={allVisiblePaths}
                forceExpanded={Boolean(query.trim())}
                onCreate={setCreateDialog}
                onRename={setRenameDialog}
                onMove={setMoveDialog}
                onDelete={setDeleteTarget}
                onOperationError={setOperationError}
              />
            ))}
          </>
        )}
      </div>

      {selectedPaths.size === 1 && (
        <div className="border-t border-surface-200 px-3 py-2 text-xs text-surface-500 dark:border-surface-800 dark:text-surface-400">
          {(() => {
            const selected = Array.from(selectedPaths)[0];
            const find = (nodes: TreeNode[]): TreeNode | null => {
              for (const node of nodes) {
                if (node.path === selected) return node;
                const child = node.children ? find(node.children) : null;
                if (child) return child;
              }
              return null;
            };
            const node = find(filteredTree);
            return node ? (
              <div className="flex items-center gap-2">
                {isPersonalInstructions(node) ? <BookOpen className="h-3.5 w-3.5" /> : fileIcon(node)}
                <span className="min-w-0 flex-1 truncate">{node.path}</span>
                <span className="shrink-0">{statusText(node)}</span>
              </div>
            ) : null;
          })()}
        </div>
      )}

      <TextInputDialog
        isOpen={!!createDialog}
        onClose={() => setCreateDialog(null)}
        onSubmit={handleCreate}
        title={createTitle}
        label={createLabel}
        placeholder={createPlaceholder}
        submitLabel="Create"
        isLoading={isMutating}
      />
      <TextInputDialog
        isOpen={!!renameDialog}
        onClose={() => setRenameDialog(null)}
        onSubmit={handleRename}
        title="Rename"
        label="Name"
        initialValue={renameDialog?.node.name || ''}
        submitLabel="Rename"
        isLoading={isMutating}
      />
      {moveDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close move dialog"
            onClick={() => setMoveDialog(null)}
          />
          <div className="relative mx-4 w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl dark:bg-surface-900">
            <div className="border-b border-surface-200 px-6 py-4 dark:border-surface-700">
              <h3 className="text-lg font-semibold text-surface-900 dark:text-white">Move Item</h3>
              <p className="mt-1 truncate text-sm text-surface-500 dark:text-surface-400">{moveDialog.node.path}</p>
            </div>
            <div className="space-y-2 px-6 py-4">
              <label htmlFor="file-tree-move-destination" className="text-sm font-medium text-surface-700 dark:text-surface-300">
                Destination folder
              </label>
              <input
                id="file-tree-move-destination"
                value={moveDialog.destination}
                onChange={(event) => setMoveDialog({ ...moveDialog, destination: event.target.value })}
                className="w-full rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-surface-700 dark:bg-surface-950 dark:text-surface-100"
                placeholder="Output/2026-06-15"
              />
              <p className="truncate text-xs text-surface-500 dark:text-surface-400">
                New path: {joinPath(moveDialog.destination.trim(), moveDialog.node.name)}
              </p>
            </div>
            <div className="flex justify-end gap-3 bg-surface-50 px-6 py-4 dark:bg-surface-800">
              <Button type="button" variant="outline" onClick={() => setMoveDialog(null)} disabled={isMutating}>
                Cancel
              </Button>
              <Button type="button" onClick={handleMove} disabled={isMutating}>
                {isMutating ? 'Moving...' : 'Move'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={`Delete ${deleteTarget?.type === 'directory' ? 'Folder' : 'File'}`}
        message={deleteTarget ? `Delete "${deleteTarget.name}"? This cannot be undone.` : ''}
        confirmLabel="Delete"
        variant="destructive"
        isLoading={isMutating}
      />
      <AlertDialog
        isOpen={!!operationError}
        onClose={() => setOperationError(null)}
        title="File Operation Failed"
        message={operationError || ''}
        type="error"
      />
    </div>
  );
}

export default FileTree;
